import * as _ from 'lodash';
import { PROJECT_EDIT_STATUS } from '@nexxt/common/types';
import { PROJECT_SAVE_INTERVAL } from '../mapping/project';

export default class AutoSavingService<T> {
    private data: T;
    private dataSynced: T;
    private type: string;
    private autoSavingInterval;
    private typingTimer: number;
    private currentEditStatus: PROJECT_EDIT_STATUS;
    private isSaving: boolean;
    private setEditStatus: (type: string, status: PROJECT_EDIT_STATUS) => void;
    private clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<void>;

    constructor(
        data: T,
        type: string,
        isSaving: boolean,
        currentEditStatus: PROJECT_EDIT_STATUS,
        setEditStatus: (type: string, status: PROJECT_EDIT_STATUS) => void,
        clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<void>
    ) {
        this.data = data;
        this.type = type;
        this.isSaving = isSaving;
        this.dataSynced = _.cloneDeep(this.data);
        this.currentEditStatus = currentEditStatus;
        this.setEditStatus = setEditStatus;
        this.clearEditedStatus = clearEditedStatus;
    }

    getData(): T {
        return this.data;
    }

    setData(data: T): void {
        if (!_.isEqual(data, this.data)) {
            this.data = _.cloneDeep(data);
        }
    }

    setSyncedData(data: T): void {
        this.dataSynced = _.cloneDeep(data);
    }
    setIsSaving(isSaving: boolean): void {
        this.isSaving = isSaving;
    }
    setCurrentEditStatus(status: PROJECT_EDIT_STATUS): void {
        this.currentEditStatus = status;
    }

    /**
     * Trigger Auto Saving interval
     * The saving function should be passed as function parameter
     * The saving function should be triggered when
     * 1. it's currently not saving AND
     * 2. the edit status is either EDITED or EDIT_REQUIRED AND
     * 3. the data is different from dataSynced
     */
    setAutoSavingInterval(
        onSave: () => Promise<void>,
        settings?: { extraCondition?: boolean }
    ): void {
        this.autoSavingInterval = setInterval(async () => {
            if (
                settings?.extraCondition ||
                (!this.isSaving &&
                    [
                        PROJECT_EDIT_STATUS.EDITED,
                        PROJECT_EDIT_STATUS.EDIT_REQUIRED
                    ].includes(this.currentEditStatus) &&
                    !_.isEqual(this.data, this.dataSynced))
            ) {
                // monitorQuestionChanges();
                await onSave();
            }
        }, PROJECT_SAVE_INTERVAL);
    }

    private monitorQuestionChanges() {
        const keys = _.keys(this.data);
        keys.forEach((key) => {
            if (!_.isEqual(this.data[key], this.dataSynced[key])) {
                console.log('DOESNOT MATCH', key);
            }
        });

        if (_.hasIn(this.data, 'setupQuestion')) {
            const sqkeys = _.keys(this.data['setupQuestion']);
            sqkeys.forEach((key) => {
                if (
                    !_.isEqual(
                        this.data['setupQuestion'][key],
                        this.dataSynced['setupQuestion'][key]
                    )
                ) {
                    console.log('DOESNOT MATCH(SETUP)', key);
                }
            });
        }

        if (_.hasIn(this.data, 'popupCards')) {
            const sqkeys = _.keys(this.data['popupCards']);
            sqkeys.forEach((key) => {
                if (
                    !_.isEqual(
                        this.data['popupCards'][key],
                        this.dataSynced['popupCards'][key]
                    )
                ) {
                    console.log('DOESNOT MATCH(popupCards)', key);
                }
            });
        }
    }

    /**
     * Mark the status to "EDITING"
     *
     */
    setEditing(): void {
        this.typingTimer && window.clearTimeout(this.typingTimer);
        this.setEditStatus(this.type, PROJECT_EDIT_STATUS.EDITING);
    }

    /**
     * This is called every edit action that users make.
     * 1. see if data look correct(data validation)
     * 2. if the current data is different from the data that synced last time, it will mark as EDITED
     * 3. if the data is same and no validation error, it simply means nothing was changed so clear edit status
     */
    setEditFlag(
        isValid: { isError: boolean },
        settings?: { editedExtraCondition?: boolean }
    ): void {
        this.typingTimer = window.setTimeout(() => {
            if (isValid.isError) {
                this.setEditStatus(
                    this.type,
                    PROJECT_EDIT_STATUS.EDIT_REQUIRED
                );
            } else if (
                !_.isEqual(this.data, this.dataSynced) ||
                settings?.editedExtraCondition
            ) {
                this.setEditStatus(this.type, PROJECT_EDIT_STATUS.EDITED);
            } else if (this.currentEditStatus === PROJECT_EDIT_STATUS.EDITING) {
                return;
            } else {
                this.clearEditedStatus();
            }
        }, 2 * PROJECT_SAVE_INTERVAL);
    }

    clearEditing(): void {
        this.typingTimer && window.clearTimeout(this.typingTimer);
        this.typingTimer = window.setTimeout(() => {
            this.clearEditedStatus();
            // this.setEditFlag();
        }, PROJECT_SAVE_INTERVAL);
    }

    /**
     * Clear auto saving interval on component unmount
     */
    clearAutoSaving(): void {
        this.autoSavingInterval && clearInterval(this.autoSavingInterval);
        this.typingTimer && clearTimeout(this.typingTimer);
        this.clearEditedStatus();
    }
}
